"use client"

import { useEffect, useState, type ComponentType } from "react"
import { CloseWrapper } from "./styles"
import IconButton from "@mui/material/IconButton"
import Box from "@mui/material/Box"

// Eager icons for layout / homepage first paint
import Icon_close from "@src/assets/icons/close.svg"
import Icon_plus from "@src/assets/icons/plus.svg"
import Icon_minus from "@src/assets/icons/minus.svg"
import Icon_language_icon from "@src/assets/icons/language-icon.svg"
import Icon_logo from "@src/assets/icons/logo.svg"
import Icon_logo_small from "@src/assets/icons/logo-small.svg"
import Icon_user from "@src/assets/icons/user.svg"
import Icon_search from "@src/assets/icons/search.svg"
import Icon_heart from "@src/assets/icons/heart.svg"
import Icon_notification from "@src/assets/icons/notification.svg"
import Icon_e_commerce from "@src/assets/icons/e-commerce.svg"
import Icon_arrow_up from "@src/assets/icons/arrow-up.svg"
import Icon_arrow_down from "@src/assets/icons/arrow-down.svg"
import Icon_arrow_right from "@src/assets/icons/arrow-right.svg"
import Icon_arrow_left from "@src/assets/icons/arrow-left.svg"
import Icon_facebook_logo from "@src/assets/icons/facebook-logo.svg"
import Icon_instagram_logo from "@src/assets/icons/instagram-logo.svg"
import Icon_tiktok_logo from "@src/assets/icons/tiktok-logo.svg"
import Icon_filter from "@src/assets/icons/filter.svg"
import Icon_caret_left from "@src/assets/icons/caret-left.svg"
import Icon_caret_right from "@src/assets/icons/caret-right.svg"
import Icon_stroke from "@src/assets/icons/stroke.svg"
import Icon_arrow_next from "@src/assets/icons/arrow-next.svg"
import Icon_shopee from "@src/assets/icons/shopee.svg"
import Icon_discount from "@src/assets/icons/discount.svg"
import Icon_freeship from "@src/assets/icons/freeship.svg"
import Icon_voucher from "@src/assets/icons/voucher.svg"
import Icon_voucher_2 from "@src/assets/icons/voucher-2.svg"
import Icon_payment from "@src/assets/icons/payment.svg"
import Icon_delivery from "@src/assets/icons/delivery.svg"
import Icon_change from "@src/assets/icons/change.svg"
import Icon_headphone from "@src/assets/icons/headphone.svg"
import Icon_sort from "@src/assets/icons/sort.svg"
import Icon_x from "@src/assets/icons/x.svg"
import Icon_eye from "@src/assets/icons/eye.svg"
import Icon_eye_slash from "@src/assets/icons/eye-slash.svg"
import Icon_gift from "@src/assets/icons/gift.svg"
import Icon_heart_bag from "@src/assets/icons/heart-bag.svg"
import Icon_hamburger from "@src/assets/icons/hamburger.svg"
import Icon_home from "@src/assets/icons/home.svg"
import Icon_flash_sale from "@src/assets/icons/flash-sale.svg"
import Icon_flat_arrow_right from "@src/assets/icons/flat-arrow-right.svg"
import Icon_shopping_bag from "@src/assets/icons/shopping-bag.svg"

type SvgComp = ComponentType<Record<string, unknown>>

const eagerIcons: Record<string, { svg: SvgComp; Wrapper?: typeof IconButton }> = {
  "close": { svg: Icon_close, Wrapper: CloseWrapper },
  "plus": { svg: Icon_plus },
  "minus": { svg: Icon_minus },
  "languageIcon": { svg: Icon_language_icon },
  "logo": { svg: Icon_logo },
  "logo-small": { svg: Icon_logo_small },
  "user": { svg: Icon_user },
  "search": { svg: Icon_search },
  "heart": { svg: Icon_heart },
  "notification": { svg: Icon_notification },
  "ecommerce": { svg: Icon_e_commerce },
  "arrow-up": { svg: Icon_arrow_up },
  "arrow-down": { svg: Icon_arrow_down },
  "arrow-right": { svg: Icon_arrow_right },
  "arrow-left": { svg: Icon_arrow_left },
  "facebook": { svg: Icon_facebook_logo },
  "instagram": { svg: Icon_instagram_logo },
  "tiktok": { svg: Icon_tiktok_logo },
  "filter": { svg: Icon_filter },
  "caret-left": { svg: Icon_caret_left },
  "caret-right": { svg: Icon_caret_right },
  "stroke": { svg: Icon_stroke },
  "arrow-next": { svg: Icon_arrow_next },
  "shopee": { svg: Icon_shopee },
  "discount": { svg: Icon_discount },
  "free-ship": { svg: Icon_freeship },
  "voucher": { svg: Icon_voucher },
  "voucher-2": { svg: Icon_voucher_2 },
  "payment": { svg: Icon_payment },
  "delivery": { svg: Icon_delivery },
  "change": { svg: Icon_change },
  "headphone": { svg: Icon_headphone },
  "sort": { svg: Icon_sort },
  "x": { svg: Icon_x },
  "eye": { svg: Icon_eye },
  "eye-slash": { svg: Icon_eye_slash },
  "gift": { svg: Icon_gift },
  "heart-bag": { svg: Icon_heart_bag },
  "hamburger": { svg: Icon_hamburger },
  "home": { svg: Icon_home },
  "flash-sale": { svg: Icon_flash_sale },
  "flat-arrow-right": { svg: Icon_flat_arrow_right },
  "shopping-bag": { svg: Icon_shopping_bag },
}

const lazyLoaders: Record<string, () => Promise<{ default: SvgComp }>> = {
  "facebook-address": () => import("@src/assets/icons/facebook-address.svg"),
  "clock": () => import("@src/assets/icons/clock.svg"),
  "link": () => import("@src/assets/icons/link.svg"),
  "document": () => import("@src/assets/icons/document.svg"),
  "rectangle": () => import("@src/assets/icons/rectangle.svg"),
  "highlight_1": () => import("@src/assets/icons/hightlight_1.svg"),
  "highlight_2": () => import("@src/assets/icons/hightlight_2.svg"),
  "highlight_3": () => import("@src/assets/icons/hightlight_3.svg"),
  "highlight_4": () => import("@src/assets/icons/hightlight_4.svg"),
  "not-found": () => import("@src/assets/icons/not-found.svg"),
  "facebook-login": () => import("@src/assets/icons/facebook-login.svg"),
  "google-login": () => import("@src/assets/icons/google-login.svg"),
  "account": () => import("@src/assets/icons/account.svg"),
  "phone": () => import("@src/assets/icons/phone.svg"),
  "email": () => import("@src/assets/icons/email.svg"),
  "pass-lock": () => import("@src/assets/icons/pass-lock.svg"),
  "about-value": () => import("@src/assets/icons/about-value.svg"),
  "about-leaf": () => import("@src/assets/icons/about-leaf.svg"),
  "about-vn-flag": () => import("@src/assets/icons/about-vn-flag.svg"),
  "branch-marker": () => import("@src/assets/icons/branch-marker.svg"),
  "branch-phone": () => import("@src/assets/icons/branch-phone.svg"),
  "branch-clock": () => import("@src/assets/icons/branch-clock.svg"),
  "branch-find-way": () => import("@src/assets/icons/branch-find-way.svg"),
  "branch-find-way-white": () => import("@src/assets/icons/branch-find-way-white.svg"),
  "location": () => import("@src/assets/icons/location.svg"),
  "location_black": () => import("@src/assets/icons/location_black.svg"),
  "package": () => import("@src/assets/icons/package.svg"),
  "setting": () => import("@src/assets/icons/setting.svg"),
  "power": () => import("@src/assets/icons/power.svg"),
  "calendar": () => import("@src/assets/icons/calendar.svg"),
  "bill": () => import("@src/assets/icons/bill.svg"),
  "sale": () => import("@src/assets/icons/sale.svg"),
  "shirt": () => import("@src/assets/icons/shirt.svg"),
  "radio": () => import("@src/assets/icons/radio.svg"),
  "radio-checked": () => import("@src/assets/icons/radio-checked.svg"),
  "edit": () => import("@src/assets/icons/edit.svg"),
  "event-clock": () => import("@src/assets/icons/event-clock.svg"),
  "event-clock-dark": () => import("@src/assets/icons/event-clock-dark.svg"),
  "event-arrow": () => import("@src/assets/icons/event-arrow.svg"),
  "event-next": () => import("@src/assets/icons/event-next.svg"),
  "event-prev": () => import("@src/assets/icons/event-prev.svg"),
  "event-heart": () => import("@src/assets/icons/event-heart.svg"),
  "event-down-arrow": () => import("@src/assets/icons/event-down-arrow.svg"),
  "check-circle-success": () => import("@src/assets/icons/check-circle.svg"),
  "cod": () => import("@src/assets/icons/cod.svg"),
  "ebank": () => import("@src/assets/icons/bank-card.svg"),
  "momo": () => import("@src/assets/icons/momo.svg"),
  "onepay": () => import("@src/assets/icons/onepay.svg"),
  "sepay": () => import("@src/assets/icons/sepay.svg"),
  "vnpay": () => import("@src/assets/icons/vn-pay.svg"),
  "note": () => import("@src/assets/icons/note.svg"),
  "care-instruction": () => import("@src/assets/icons/care-instruction.svg"),
  "purchase-policy": () => import("@src/assets/icons/purchase-policy.svg"),
  "complaints": () => import("@src/assets/icons/complaints.svg"),
  "warranty": () => import("@src/assets/icons/warranty.svg"),
}

const lazyCache = new Map<string, SvgComp>()

function useIcon(name?: string): { svg?: SvgComp; Wrapper: typeof IconButton } {
  const eager = name ? eagerIcons[name] : undefined
  const [lazySvg, setLazySvg] = useState<SvgComp | undefined>(() =>
    name ? lazyCache.get(name) : undefined
  )

  useEffect(() => {
    if (!name || eager || !lazyLoaders[name]) return
    if (lazyCache.has(name)) {
      setLazySvg(() => lazyCache.get(name))
      return
    }
    let cancelled = false
    lazyLoaders[name]().then(mod => {
      if (cancelled) return
      lazyCache.set(name, mod.default)
      setLazySvg(() => mod.default)
    })
    return () => {
      cancelled = true
    }
  }, [name, eager])

  if (eager) {
    return { svg: eager.svg, Wrapper: eager.Wrapper || IconButton }
  }
  return { svg: lazySvg, Wrapper: IconButton }
}

const Icons = props => {
  const { name, type, onClick, sx, ...rest } = props
  const { svg: SvgComponent, Wrapper } = useIcon(name)

  if (!SvgComponent) {
    return <Box sx={{ width: 24, height: 24, ...sx }} aria-hidden />
  }

  if (type === "button") {
    return (
      <Wrapper
        sx={{
          cursor: "pointer",
          borderRadius: "50%",
          width: "auto",
          height: "auto",
          ...sx
        }}
        color="inherit"
        aria-label={name}
        onClick={onClick}
        {...rest}
      >
        <SvgComponent />
      </Wrapper>
    )
  }

  return (
    <Box onClick={onClick} sx={sx} display="flex" alignItems="center" {...rest}>
      <SvgComponent />
    </Box>
  )
}

export default Icons
